// src/ui/overlays/ControlsTutorialCard.tsx — P7 Plan 09 (UI-07, D-06).
//
// A single static labelled diagram card (NOT live coach-marks, D-06) shown once
// before the first round. Standalone; App.tsx wiring is Plan 10. Follows the
// InstallHint one-time `hunter:*` localStorage idiom verbatim — fail-open on
// read, silent on write — swapping the key to `hunter:tutorial.seen`.
//
//   - Non-preview: if the flag === "1" the card never mounts; first dismiss sets
//     the flag and hides; subsequent mounts are suppressed.
//   - Preview (title "How to play"): mounts regardless of the flag; dismiss
//     NEVER reads or writes the flag (D-06).
//
// Source: 07-UI-SPEC §Surface 4; PATTERNS.md ControlsTutorialCard analog.

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import './controls-tutorial-card.css';

const SEEN_KEY = 'hunter:tutorial.seen';

function hasSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    // private mode — fail open (show the tutorial once).
    return false;
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // noop — same rationale as read.
  }
}

export interface ControlsTutorialCardProps {
  /** Title "How to play" preview: mount regardless of the flag and never touch
   *  it on dismiss. */
  preview?: boolean;
  onDismiss: () => void;
}

interface Callout {
  label: string;
  glyph: JSX.Element;
}

// Inline-SVG pictographs reusing the live HUD control glyphs (joystick / torch /
// hide / drop). Zero deps (existing pattern — TorchButton, char-select chevron).
// aria-hidden — the text label beneath each is the accessible name.
const CALLOUTS: Callout[] = [
  {
    label: 'Move',
    glyph: (
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <circle cx="16" cy="16" r="13" fill="none" stroke="#2a4a62" strokeWidth="2" />
        <circle cx="16" cy="16" r="6" fill="#e8a838" />
      </svg>
    ),
  },
  {
    label: 'Torch',
    glyph: (
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <path d="M16 4 L26 24 A12 12 0 0 1 6 24 Z" fill="#e8a838" opacity="0.85" />
      </svg>
    ),
  },
  {
    label: 'Hide',
    glyph: (
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <path
          d="M3 16 C8 8 24 8 29 16 C24 24 8 24 3 16 Z"
          fill="none"
          stroke="#f0ead8"
          strokeWidth="2"
        />
        <circle cx="16" cy="16" r="4" fill="#f0ead8" />
      </svg>
    ),
  },
  {
    label: 'Drop',
    glyph: (
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <path d="M16 6 V20" stroke="#f0ead8" strokeWidth="2" strokeLinecap="round" />
        <path
          d="M10 16 L16 22 L22 16"
          fill="none"
          stroke="#f0ead8"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="8" y="25" width="16" height="2" rx="1" fill="#2a4a62" />
      </svg>
    ),
  },
];

export function ControlsTutorialCard({
  preview = false,
  onDismiss,
}: ControlsTutorialCardProps): JSX.Element | null {
  const [visible, setVisible] = useState<boolean>(() => preview || !hasSeen());
  const btnRef = useRef<HTMLButtonElement>(null);

  const dismiss = useCallback((): void => {
    if (!preview) markSeen();
    setVisible(false);
    onDismiss();
  }, [preview, onDismiss]);

  // Focus the dismiss CTA when shown (07-UI-SPEC §Surface 4 ARIA).
  useEffect(() => {
    if (visible) btnRef.current?.focus();
  }, [visible]);

  // Escape dismisses (counts as seen on the first-run path via dismiss()).
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return (): void => window.removeEventListener('keydown', onKey);
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div className="controls-tutorial-backdrop" data-testid="controls-tutorial-backdrop">
      <div
        className="controls-tutorial-card"
        data-testid="controls-tutorial-card"
        role="dialog"
        aria-modal="true"
        aria-label="How to play"
      >
        <h2 className="controls-tutorial-card__title">How to play</h2>
        <p className="controls-tutorial-card__intro">
          Tap and drag to move. Here&apos;s the rest.
        </p>
        <div className="controls-tutorial-card__grid">
          {CALLOUTS.map((c) => (
            <div className="controls-tutorial-card__cell" key={c.label}>
              <span className="controls-tutorial-card__glyph">{c.glyph}</span>
              <span className="controls-tutorial-card__label">{c.label}</span>
            </div>
          ))}
        </div>
        <button
          ref={btnRef}
          type="button"
          className="controls-tutorial-card__cta"
          data-testid="controls-tutorial-dismiss"
          onClick={dismiss}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
